import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, Coins, ArrowLeft, Clock } from 'lucide-react';
import axios from 'axios';
import '../styles/payment.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Handles the return-from-checkout flow for BOTH Stripe and MercadoPago.
 * We detect the provider from:
 *   • ?provider=mercadopago query param (set by us in back_urls), OR
 *   • presence of ?preference_id / ?collection_id (MercadoPago's own params)
 * Otherwise we fall back to the legacy Stripe ?session_id flow.
 */
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const providerParam = searchParams.get('provider');
  const stripeSessionId = searchParams.get('session_id');
  const mpPreferenceId =
    searchParams.get('preference_id') || searchParams.get('pref_id');
  const mpCollectionStatus = searchParams.get('collection_status'); // approved|pending|rejected
  const mpPending = searchParams.get('pending') === '1';

  const isMercadoPago =
    providerParam === 'mercadopago' || !!mpPreferenceId || !!mpCollectionStatus;

  const [status, setStatus] = useState('polling'); // polling|success|pending|expired|error|timeout
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    let attempts = 0;
    const stripeMaxAttempts = 8;
    const mpMaxAttempts = 20; // ~60s — MP webhooks can lag a few seconds
    const pollInterval = 3000;

    const pollStripe = async () => {
      if (!stripeSessionId) {
        setStatus('error');
        setError('Falta el identificador de la sesión.');
        return;
      }
      try {
        const res = await axios.get(
          `${API}/payments/checkout/status/${stripeSessionId}`
        );
        const data = res.data;
        setPaymentData(data);
        if (data.payment_status === 'paid') return setStatus('success');
        if (data.status === 'expired') return setStatus('expired');
        if (++attempts < stripeMaxAttempts) return setTimeout(pollStripe, pollInterval);
        setStatus('timeout');
      } catch (_err) {
        if (++attempts < stripeMaxAttempts) return setTimeout(pollStripe, pollInterval);
        setStatus('error');
        setError('Error al verificar el pago');
      }
    };

    const pollMercadoPago = async () => {
      if (!mpPreferenceId) {
        // MP didn't give us a preference_id — still show a friendly message
        setStatus(mpPending ? 'pending' : 'timeout');
        setError(
          'Tu pago está siendo procesado. Te notificaremos cuando se acredite.'
        );
        return;
      }
      try {
        // Public endpoint — does NOT require Clerk auth, so it survives
        // session loss across the MP redirect (Safari SameSite etc).
        const res = await axios.get(
          `${API}/payments/mercadopago/status/${mpPreferenceId}`
        );
        const data = res.data;
        setPaymentData(data);

        if (data.payment_status === 'approved' || data.status === 'completed') {
          return setStatus('success');
        }
        if (data.payment_status === 'rejected' || data.status === 'failed') {
          return setStatus('expired');
        }
        if (++attempts < mpMaxAttempts) {
          return setTimeout(pollMercadoPago, pollInterval);
        }
        // Exhausted polls: treat as pending (MP webhook will eventually
        // promote it; user gets clear message).
        setStatus('pending');
      } catch (_err) {
        if (++attempts < mpMaxAttempts) {
          return setTimeout(pollMercadoPago, pollInterval);
        }
        setStatus('error');
        setError('Error al verificar el pago con MercadoPago');
      }
    };

    if (isMercadoPago) pollMercadoPago();
    else pollStripe();
  }, [isMercadoPago, stripeSessionId, mpPreferenceId, mpPending]);

  const goToStudio = () => navigate('/studio');
  const providerLabel = isMercadoPago ? 'MercadoPago' : 'Stripe';

  return (
    <div className="payment-page" data-testid="payment-success-page">
      <div className="payment-card">
        {status === 'polling' && (
          <>
            <div className="payment-icon polling">
              <Loader2 size={48} className="spin-icon" />
            </div>
            <h1>Verificando pago...</h1>
            <p>Estamos confirmando tu pago con {providerLabel}. Esto tomará solo unos segundos.</p>
          </>
        )}

        {status === 'success' && paymentData && (
          <>
            <div className="payment-icon success">
              <CheckCircle size={48} />
            </div>
            <h1>¡Pago exitoso!</h1>
            <p>Tus créditos han sido añadidos a tu cuenta.</p>
            <div className="payment-details">
              <div className="payment-detail-row">
                <Coins size={16} />
                <span>Créditos añadidos:</span>
                <strong>{paymentData.credits}</strong>
              </div>
              <div className="payment-detail-row">
                <span>Paquete:</span>
                <strong>{paymentData.package_id}</strong>
              </div>
            </div>
            <button
              className="payment-btn primary"
              onClick={goToStudio}
              data-testid="go-to-studio-btn"
            >
              <ArrowLeft size={16} />
              Volver al Studio
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="payment-icon polling">
              <Clock size={48} />
            </div>
            <h1>Pago pendiente</h1>
            <p>
              Tu pago está siendo procesado por {providerLabel}. Te acreditaremos
              los créditos automáticamente en cuanto se confirme.
            </p>
            <button className="payment-btn primary" onClick={goToStudio}>
              <ArrowLeft size={16} />
              Volver al Studio
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="payment-icon error">
              <AlertCircle size={48} />
            </div>
            <h1>Pago no completado</h1>
            <p>El pago fue rechazado o la sesión expiró. Podés intentar de nuevo.</p>
            <button className="payment-btn primary" onClick={goToStudio}>
              <ArrowLeft size={16} />
              Volver al Studio
            </button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="payment-icon warning">
              <AlertCircle size={48} />
            </div>
            <h1>Verificación pendiente</h1>
            <p>{error || 'La verificación del pago está tomando más tiempo de lo esperado.'}</p>
            <button className="payment-btn primary" onClick={goToStudio}>
              <ArrowLeft size={16} />
              Volver al Studio
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
